import * as Sentry from '@sentry/react';
import create from 'zustand';
import { devtools } from 'zustand/middleware';

import authApi from 'api/auth';
import { clearAuthTokens, getAccessToken, getRefreshToken, setAuthTokens } from 'axios-jwt';
import { isEqual } from 'es-toolkit/compat';
import { useLayoutStore } from 'store/layout';
import { APIError, LoginAsAdminProps, LoginAsPartnerAdminProps, User, UserCredentials } from 'types';
import { extractUserFromToken, getRoleByNumber } from 'utils/_utils/session';
import { clearReferralCode } from 'utils/publicBranding';

export interface AuthStore {
  user: User | null;
  otpToken: string | null;
  loading: boolean;
  error: APIError | null;
  loginAsAdmin: LoginAsAdminProps;
  loginAsPartnerAdmin: LoginAsPartnerAdminProps;
  loginUser: (credentials: UserCredentials) => Promise<void | string>;
  loginUserWithAccessToken: (entityId?: string) => void;
  loginUserWithRefreshToken: (entityId?: string) => Promise<void>;
  verifyOtp: (otpCode: string, otpToken: string) => void;
  loginUserAsAdmin: (props: LoginAsAdminProps) => void;
  loginUserAsPartnerAdmin: (props: LoginAsPartnerAdminProps) => void;
  logoutUserAsAdmin: () => void;
  logoutUserAsPartnerAdmin: () => void;
  logoutUser: (redirect?: boolean) => void;
  setOtpEnabled: (enabled: boolean) => void;
  reset: () => void;
}

export const useAuthStore = create<AuthStore>()(
  devtools((set, getState, store) => ({
    user: null,
    otpToken: null,
    loading: false,
    error: null,
    loginAsAdmin: {
      clientEntityId: null,
      clientName: '',
      previousUser: null
    },
    loginAsPartnerAdmin: {
      partnerEntityId: null,
      partnerName: '',
      previousUser: null
    },
    loginUser: ({ email, password }) =>
      new Promise((resolve, reject) => {
        set({ loading: true, error: null });
        authApi
          .login(email, password)
          .then(({ access, refresh, otp_token: otpToken }) => {
            if (otpToken) return set({ otpToken, loading: false });

            if (access && refresh) {
              const extractedUser = extractUserFromToken(access);

              Sentry.setUser({
                id: extractedUser.user_id || 'unknown',
                email: extractedUser.email || 'unknown'
              });

              // If the user is a partneradmin and OTP is not enabled, we need to show a modal to the user to enable OTP
              if (getRoleByNumber(extractedUser.role) === 'firmadmin' && !extractedUser.otp_enabled) {
                set({ loading: false });
                return resolve(access);
              }

              setAuthTokens({ accessToken: access, refreshToken: refresh });
              useLayoutStore.getState().applyTheme(access);

              // The public co-branding is keyed by the referral code; a completed login ends it.
              clearReferralCode();

              set({ user: extractedUser, loading: false });

              return resolve();
            }

            set({ error: { description: 'Unknown login error' }, loading: false });
            return reject();
          })
          .catch((error: APIError) => {
            reject(error.description);
            set({ error, loading: false });
          });
      }),
    loginUserWithAccessToken: entityId => {
      const user = extractUserFromToken(getAccessToken());
      set({ user: { ...user, entity_id: entityId || user.entity_id || null } });
    },
    loginUserWithRefreshToken: entityId =>
      authApi
        .refreshLogin()
        .then(({ access }) => {
          const refreshToken = getRefreshToken();

          if (access) {
            clearAuthTokens();
            setAuthTokens({ accessToken: access, refreshToken: refreshToken || '' });

            useLayoutStore.getState().applyTheme(access);

            const loggedUser = extractUserFromToken(access);

            return set({
              user: {
                ...loggedUser,
                entity_id: entityId || loggedUser.entity_id || null
              },
              loading: false
            });
          }

          return set({ error: { description: 'Unknown login error' }, loading: false });
        })
        .catch((error: APIError) => set({ error, loading: false })),
    verifyOtp: (otpCode, otpToken) => {
      set({ loading: true, error: null });
      authApi
        .verifyOtp(otpToken, otpCode)
        .then(({ access, refresh }) => {
          setAuthTokens({ accessToken: access, refreshToken: refresh });
          useLayoutStore.getState().applyTheme(access);

          // The public co-branding is keyed by the referral code; a completed login ends it.
          clearReferralCode();

          set({
            user: extractUserFromToken(access),
            otpToken: null,
            loading: false
          });
        })
        .catch((error: APIError) => set({ error, loading: false }));
    },
    loginUserAsAdmin: ({ clientEntityId, clientName, previousUser, loginAsPartnerAdmin, afterLoginCallback }) => {
      set(prev => ({
        user: {
          ...previousUser!,
          entity_id: clientEntityId
        },
        loginAsAdmin: {
          clientEntityId,
          clientName,
          previousUser
        },
        loginAsPartnerAdmin: loginAsPartnerAdmin ? { ...loginAsPartnerAdmin } : prev.loginAsPartnerAdmin
      }));

      if (afterLoginCallback) afterLoginCallback();
    },
    loginUserAsPartnerAdmin: ({ partnerEntityId, partnerName, previousUser }) => {
      set({
        user: {
          ...previousUser!,
          entity_id: partnerEntityId
        },
        loginAsPartnerAdmin: {
          partnerEntityId,
          partnerName,
          previousUser
        }
      });
    },
    logoutUserAsAdmin: () => {
      useLayoutStore.getState().setNoScansInCompany(false);
      set(state => ({
        ...state,
        user: {
          ...state.loginAsAdmin.previousUser!,
          client_id: null
        },
        loginAsAdmin: {
          clientEntityId: null,
          clientName: '',
          previousUser: null
        }
      }));
    },
    logoutUserAsPartnerAdmin: () => {
      set(state => ({
        ...state,
        user: {
          ...state.loginAsPartnerAdmin.previousUser!,
          client_id: null
        },
        loginAsPartnerAdmin: {
          partnerEntityId: null,
          partnerName: '',
          previousUser: null
        }
      }));
    },
    logoutUser: (redirect = false) => {
      clearAuthTokens();
      Sentry.setUser(null);

      getState().reset();
      useLayoutStore.getState().reset();

      if (redirect) {
        window.location.href = window.location.origin;
      }
    },
    setOtpEnabled: (enabled: boolean) => {
      set(state => ({
        ...state,
        user: {
          ...state.user!,
          otp_enabled: enabled
        }
      }));
    },
    reset: () => {
      if (isEqual(getState(), store.getInitialState())) return;

      set(store.getInitialState());
    }
  }))
);
